/**
 * @fileoverview Doctor Dashboard API Endpoints
 * @description API client functions for doctor role endpoints
 * @author MCHD Room Scheduler Team
 * @created 07-10-2025
 * @complies @rest-api-excellence.mdc, @typescript-quality.mdc
 */

import { api } from '../index';
import type {
  DoctorDashboardKPIs,
  DoctorAssignment,
  WeeklyScheduleData,
  UpcomingAssignmentsFilters,
  ApiResponse,
} from '../../types/doctor';

/**
 * Get doctor dashboard KPIs
 * @returns Promise with KPIs data
 * @description Fetches upcoming sessions, buildings, rooms, and weekly breakdown
 * @throws {Error} If API request fails
 */
export async function getDoctorKPIs(): Promise<ApiResponse<DoctorDashboardKPIs>> {
  try {
    const response = await api.get('/dashboards/doctor/kpis');
    return response;
  } catch (error) {
    console.error('[Doctor API] Error fetching KPIs:', error);
    throw error;
  }
}

/**
 * Get doctor's upcoming assignments
 * @param filters - Optional filters (startDate, endDate, buildingId, sessionLabel)
 * @returns Promise with list of assignments
 * @description Fetches upcoming published assignments with optional filters
 * @throws {Error} If API request fails
 */
export async function getUpcomingAssignments(
  filters?: UpcomingAssignmentsFilters
): Promise<ApiResponse<DoctorAssignment[]>> {
  try {
    // Build query string from filters
    const queryParams = new URLSearchParams();
    
    if (filters?.startDate) {
      queryParams.append('startDate', filters.startDate);
    }
    if (filters?.endDate) {
      queryParams.append('endDate', filters.endDate);
    }
    if (filters?.buildingId) {
      queryParams.append('buildingId', filters.buildingId);
    }
    if (filters?.sessionLabel) {
      queryParams.append('sessionLabel', filters.sessionLabel);
    }

    const queryString = queryParams.toString();
    const url = `/dashboards/doctor/upcoming-assignments${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(url);
    
    // Convert date strings to Date objects
    if (response.success && response.data) {
      response.data = response.data.map((assignment: any) => ({
        ...assignment,
        date: new Date(assignment.date),
      }));
    }
    
    return response;
  } catch (error) {
    console.error('[Doctor API] Error fetching upcoming assignments:', error);
    throw error;
  }
}

/**
 * Get doctor's weekly schedule
 * @param weekStart - Week start date (ISO 8601 string or Date object)
 * @returns Promise with assignments grouped by date
 * @description Fetches assignments for a specific week grouped by day
 * @throws {Error} If API request fails
 */
export async function getWeeklySchedule(
  weekStart: string | Date
): Promise<ApiResponse<WeeklyScheduleData>> {
  try {
    // Convert Date object to ISO string if needed
    const weekStartStr = typeof weekStart === 'string' 
      ? weekStart 
      : weekStart.toISOString().split('T')[0]; // YYYY-MM-DD

    const response = await api.get(
      `/dashboards/doctor/weekly-schedule?weekStart=${weekStartStr}`
    );
    
    // Convert date strings to Date objects in nested arrays
    if (response.success && response.data) {
      const convertedData: WeeklyScheduleData = {};
      
      for (const [dateKey, assignments] of Object.entries(response.data)) {
        convertedData[dateKey] = (assignments as any[]).map((assignment: any) => ({
          ...assignment,
          date: new Date(assignment.date),
        }));
      }
      
      response.data = convertedData;
    }
    
    return response;
  } catch (error) {
    console.error('[Doctor API] Error fetching weekly schedule:', error);
    throw error;
  }
}

/**
 * Calculate week start date (Sunday) for a given date
 * @param date - Date object or ISO string
 * @returns Date object representing the Sunday of that week
 * @description Utility function for calendar view navigation
 */
export function getWeekStart(date: Date | string): Date {
  const d = typeof date === 'string' ? new Date(date) : new Date(date);
  const day = d.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const diff = d.getDate() - day; // Subtract day index to get to Sunday
  return new Date(d.setDate(diff));
}

/**
 * Get current week's start date (Sunday)
 * @returns Date object representing the Sunday of current week
 */
export function getCurrentWeekStart(): Date {
  return getWeekStart(new Date());
}

/**
 * Get next week's start date (Sunday)
 * @param currentWeekStart - Optional current week start (defaults to current week)
 * @returns Date object representing the Sunday of next week
 */
export function getNextWeekStart(currentWeekStart?: Date): Date {
  const weekStart = currentWeekStart || getCurrentWeekStart();
  const nextWeek = new Date(weekStart);
  nextWeek.setDate(weekStart.getDate() + 7);
  return nextWeek;
}

/**
 * Get previous week's start date (Sunday)
 * @param currentWeekStart - Optional current week start (defaults to current week)
 * @returns Date object representing the Sunday of previous week
 */
export function getPreviousWeekStart(currentWeekStart?: Date): Date {
  const weekStart = currentWeekStart || getCurrentWeekStart();
  const prevWeek = new Date(weekStart);
  prevWeek.setDate(weekStart.getDate() - 7);
  return prevWeek;
}

/**
 * Format date to ISO 8601 date string (YYYY-MM-DD)
 * @param date - Date object
 * @returns ISO 8601 date string
 */
export function formatDateISO(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * Get date range for current week (Sunday-Saturday)
 * @returns Object with start and end dates as ISO strings
 */
export function getCurrentWeekRange(): { startDate: string; endDate: string } {
  const weekStart = getCurrentWeekStart();
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6); // Saturday
  
  return {
    startDate: formatDateISO(weekStart),
    endDate: formatDateISO(weekEnd),
  };
}

/**
 * Get date range for next week (Sunday-Saturday)
 * @returns Object with start and end dates as ISO strings
 */
export function getNextWeekRange(): { startDate: string; endDate: string } {
  const weekStart = getNextWeekStart();
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6); // Saturday
  
  return {
    startDate: formatDateISO(weekStart),
    endDate: formatDateISO(weekEnd),
  };
}

